import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useDataRefresh } from "@/hooks/useDataRefresh";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, MessageCircle, Bell, Clock, MapPin, Plus, CalendarRange, CalendarDays, CalendarClock, LayoutGrid, Eye, Layers, Send, ShieldOff, Check, Sparkles } from "lucide-react";
import KaiPlannerDialog from "./KaiPlannerDialog";
import StudentSummaryDialog from "./StudentSummaryDialog";
import StudentAvailabilityDialog from "./StudentAvailabilityDialog";
import MonthGrid, { isoFromDate } from "./MonthGrid";
import DayColumnsGrid from "./DayColumnsGrid";
import AppointmentDialog from "./AppointmentDialog";
import CicloDgtPanel from "@/components/autoescuela/CicloDgtPanel";
import { preferJoseAlfredo } from "@/utils/teacherOrder";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Users2, ChevronDown, RefreshCw, Copy, Pencil, Trash2, CircleCheck, X } from "lucide-react";
import AppointmentFilters from "./AppointmentFilters";
import { StatusBadges, AppointmentActions } from "./AppointmentCard";
import { applyFilter } from "./AppointmentsTab";
import CounterProposeDialog from "./CounterProposeDialog";
import WhatsAppPreviewDialog from "./WhatsAppPreviewDialog";
import BulkWhatsAppDialog from "./BulkWhatsAppDialog";
import TeacherUnavailabilityDialog from "./TeacherUnavailabilityDialog";
import useAppointmentActions from "@/hooks/useAppointmentActions";
import { formatDateES, todayISO, computeClassBreakdown, isExamAppointment, studentCalendarLabelWide, studentShortLabel, appointmentRouteLabel, unavailColor, examTextClass, examBadgeClass, UNAVAIL_COLORS } from "@/lib/utils-app";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Monday-anchored start of week
function startOfWeek(d) {
  const x = new Date(d);
  const offset = (x.getDay() + 6) % 7;
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - offset);
  return x;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function ViewSwitcher({ value, onChange }) {
  const options = [
    { key: "day", label: "Hoy", icon: CalendarClock, testid: "cal-view-day" },
    { key: "week", label: "Semana", icon: CalendarDays, testid: "cal-view-week" },
    { key: "biweek", label: "Quincena", icon: CalendarRange, testid: "cal-view-biweek" },
    { key: "month", label: "Mes", icon: LayoutGrid, testid: "cal-view-month" },
  ];
  return (
    <div className="inline-flex rounded-xl border border-neutral-200 bg-white p-1 shadow-sm" role="tablist">
      {options.map((o) => {
        const active = value === o.key;
        const Icon = o.icon;
        return (
          <button
            key={o.key}
            type="button"
            data-testid={o.testid}
            onClick={() => onChange(o.key)}
            className={
              "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold transition-colors " +
              (active
                ? "bg-orange-500 text-white shadow-sm"
                : "text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50")
            }
          >
            <Icon className="h-3.5 w-3.5" />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function rangeLabel(view, cursor) {
  if (view === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  if (view === "day") {
    const dow = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][cursor.getDay()];
    return `${dow}, ${String(cursor.getDate()).padStart(2, "0")} ${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  }
  const days = view === "week" ? 7 : 14;
  const end = new Date(cursor);
  end.setDate(cursor.getDate() + days - 1);
  const fmt = (d) => `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()].slice(0, 3)}`;
  return `${fmt(cursor)} — ${fmt(end)} ${end.getFullYear()}`;
}

function CalendarToolbar({ view, onViewChange, onPrev, onNext, onToday, onNew, onKai, onRefresh, refreshing, label }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 justify-between">
      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="cal-prev" onClick={onPrev} className="btn-secondary !h-10 !px-3" aria-label="Anterior">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button data-testid="cal-next" onClick={onNext} className="btn-secondary !h-10 !px-3" aria-label="Siguiente">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button data-testid="cal-today" onClick={onToday} className="btn-secondary !h-10">Hoy</button>
        <div className="text-base sm:text-lg font-bold capitalize ml-1" data-testid="cal-range-label">{label}</div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <ViewSwitcher value={view} onChange={onViewChange} />
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="cal-refresh"
          title="Actualizar datos del calendario"
          className="btn-secondary !h-10 !px-3"
          aria-label="Actualizar"
        >
          <RefreshCw className={"h-4 w-4 " + (refreshing ? "animate-spin" : "")} />
        </button>
        <div className="relative group">
          <button
            onClick={onNew}
            data-testid="cal-create-appointment"
            className="btn-primary !h-10"
          >
            <Plus className="h-4 w-4" />
            Nueva cita
          </button>
          <div className="absolute right-0 top-full pt-1 z-40 hidden group-hover:block group-focus-within:block">
            <button
              type="button"
              onClick={onKai}
              data-testid="cal-open-kai-planner"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white h-9 px-3 text-xs font-bold shadow-lg transition-colors"
            >
              <Sparkles className="h-3.5 w-3.5 text-orange-400" />
              Kai Planificador
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CalendarLegend() {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-xs text-neutral-600 flex-wrap">
        <span className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider mr-1">Estado:</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Pendiente</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-green-500" /> Confirmada</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> Cancelada</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500" /> Cambio solicitado</span>
      </div>
      <div className="flex items-center gap-4 text-xs text-neutral-600 flex-wrap">
        <span className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider mr-1">Recordatorio:</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500 ring-1 ring-red-300" /> &lt; 24h sin enviar</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-orange-500 ring-1 ring-orange-300" /> 24-48h sin enviar</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-emerald-300" /> Enviado</span>
      </div>
    </div>
  );
}

// V1.48.1 · El bloqueo se muestra como una cita más (tarjeta tamaño completo,
// fondo gris) intercalada cronológicamente, con acciones: Confirmar, Marcar
// asistida, Enviar WhatsApp (avisar a otro profe/dirección/familia) y Eliminar.
function UnavailabilityCard({ u, onReload }) {
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ title: "", date: "", time: "", end_time: "" });
  const status = (u.status || "pendiente").toLowerCase();
  const setStatus = async (next) => {
    setBusy(next);
    try {
      await api.patch(`/teacher-unavailability/${u.id}`, { status: next });
      toast.success(next === "confirmada" ? "Bloqueo confirmado" : next === "asistida" ? "Bloqueo marcado como asistido" : "Bloqueo actualizado");
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo actualizar");
    } finally {
      setBusy("");
    }
  };
  const remove = async () => {
    if (!window.confirm("¿Eliminar este bloqueo de No disponible?")) return;
    setBusy("delete");
    try {
      await api.delete(`/teacher-unavailability/${u.id}`);
      toast.success("Bloqueo eliminado");
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo eliminar");
    } finally {
      setBusy("");
    }
  };
  const removeSeries = async () => {
    if (!window.confirm(`¿Eliminar TODA la serie «${u.title || "No disponible"}» (${u.all_day ? "día completo" : `${u.time}–${u.end_time}`})? Se borrarán todos los días de la repetición.`)) return;
    setBusy("delete-series");
    try {
      const { data } = await api.delete(`/teacher-unavailability/${u.id}/series`);
      toast.success(`Serie eliminada — ${data?.deleted ?? ""} bloqueos borrados`);
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo eliminar la serie");
    } finally {
      setBusy("");
    }
  };
  const startEdit = () => {
    setEdit({ title: u.title || "", date: u.date, time: u.time || "09:00", end_time: u.end_time || "10:00", color: u.color || "gris" });
    setEditing(true);
  };
  const saveEdit = async (scope = "one") => {
    setBusy("edit");
    try {
      const { data } = await api.put(`/teacher-unavailability/${u.id}`, edit, { params: { scope } });
      toast.success(scope === "series"
        ? `Serie modificada (${data?.updated ?? 0} días actualizados)`
        : "Bloqueo modificado");
      setEditing(false);
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo modificar");
    } finally {
      setBusy("");
    }
  };
  const sendWhatsApp = () => {
    const msg =
      `🚫 Bloqueo de agenda — ${u.teacher || ""}\n` +
      `📅 ${formatDateES(u.date)}${u.all_day ? " · Día completo" : ` · ${u.time}${u.end_time ? `–${u.end_time}` : ""}`}\n` +
      `📝 ${u.title || "No disponible"}` +
      (u.notes ? `\n${u.notes}` : "");
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };
  const STATUS_CHIP = {
    pendiente: "bg-amber-100 text-amber-800",
    confirmada: "bg-green-100 text-green-800",
    asistida: "bg-emerald-600 text-white",
  };
  return (
    <div
      data-testid={`daydetail-unavail-${u.id}`}
      className={"rounded-xl px-3 py-2.5 space-y-2 " + (unavailColor(u.color).key === "gris"
        ? "border border-neutral-300 bg-neutral-200/60"
        : "border " + unavailColor(u.color).chip.replace("ring-1", "border"))}
      data-color={u.color || "gris"}
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-700 text-white text-[10px] font-semibold px-2 py-0.5">
          <ShieldOff className="h-3 w-3" /> Bloqueo profesor
        </span>
        <span className={"inline-flex items-center rounded-full text-[10px] font-semibold px-2 py-0.5 capitalize " + (STATUS_CHIP[status] || STATUS_CHIP.pendiente)}>
          {status}
        </span>
        {u.all_day && (
          <span className="inline-flex items-center rounded-full bg-neutral-700 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
            Día completo
          </span>
        )}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm text-neutral-800">
            {u.title || "No disponible"}
            <span className="ml-2 text-neutral-500 font-normal">· {u.teacher || "—"}</span>
          </div>
          <div className="text-xs text-neutral-600 mt-0.5 flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            <span className="font-mono tabular-nums font-semibold">
              {u.all_day ? "Todo el día" : `${u.time}${u.end_time ? `–${u.end_time}` : ""}`}
            </span>
            {u.notes && <span className="text-neutral-500 truncate"> — {u.notes}</span>}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-neutral-300/60">
        {status === "pendiente" && (
          <button
            type="button"
            data-testid={`daydetail-unavail-confirm-${u.id}`}
            disabled={!!busy}
            onClick={() => setStatus("confirmada")}
            className="btn-ghost border border-neutral-300 bg-white text-green-700 !h-8 !px-2.5 text-xs disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" /> Confirmar
          </button>
        )}
        {status !== "asistida" && (
          <button
            type="button"
            data-testid={`daydetail-unavail-attend-${u.id}`}
            disabled={!!busy}
            onClick={() => setStatus("asistida")}
            className="btn-ghost border border-neutral-300 bg-white text-neutral-700 !h-8 !px-2.5 text-xs disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" /> Marcar asistida
          </button>
        )}
        <button
          type="button"
          data-testid={`daydetail-unavail-whatsapp-${u.id}`}
          onClick={sendWhatsApp}
          className="btn-ghost border border-neutral-300 bg-white text-emerald-700 !h-8 !px-2.5 text-xs"
          title="Avisar por WhatsApp a otro profe, dirección o familia"
        >
          <MessageCircle className="h-3.5 w-3.5" /> Enviar WhatsApp
        </button>
        <button
          type="button"
          data-testid={`daydetail-unavail-edit-${u.id}`}
          disabled={!!busy}
          onClick={() => (editing ? setEditing(false) : startEdit())}
          className="btn-ghost border border-neutral-300 bg-white text-neutral-700 !h-8 !px-2.5 text-xs disabled:opacity-50"
        >
          Modificar
        </button>
        <button
          type="button"
          data-testid={`daydetail-unavail-delete-${u.id}`}
          disabled={!!busy}
          onClick={remove}
          className="btn-ghost border border-red-200 bg-white text-red-600 !h-8 !px-2.5 text-xs disabled:opacity-50 ml-auto"
        >
          Eliminar
        </button>
        <button
          type="button"
          data-testid={`daydetail-unavail-delete-series-${u.id}`}
          disabled={!!busy}
          onClick={removeSeries}
          title="Elimina todos los días de esta repetición (mismo motivo y franja)"
          className="btn-ghost border border-red-300 bg-red-50 text-red-700 !h-8 !px-2.5 text-xs font-bold disabled:opacity-50"
        >
          Eliminar serie
        </button>
      </div>
      {editing && (
        <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-2 flex flex-wrap items-end gap-2" data-testid={`daydetail-unavail-edit-form-${u.id}`}>
          <label className="text-[10px] font-semibold text-neutral-600">
            Motivo
            <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
              className="block h-8 w-36 rounded-lg border border-neutral-300 px-2 text-xs bg-white"
              data-testid={`daydetail-unavail-edit-title-${u.id}`} />
          </label>
          <label className="text-[10px] font-semibold text-neutral-600">
            Fecha
            <input type="date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })}
              className="block h-8 rounded-lg border border-neutral-300 px-2 text-xs bg-white"
              data-testid={`daydetail-unavail-edit-date-${u.id}`} />
          </label>
          {!u.all_day && (
            <>
              <label className="text-[10px] font-semibold text-neutral-600">
                Inicio
                <input type="time" value={edit.time} onChange={(e) => setEdit({ ...edit, time: e.target.value })}
                  className="block h-8 rounded-lg border border-neutral-300 px-2 text-xs bg-white"
                  data-testid={`daydetail-unavail-edit-time-${u.id}`} />
              </label>
              <label className="text-[10px] font-semibold text-neutral-600">
                Fin
                <input type="time" value={edit.end_time} onChange={(e) => setEdit({ ...edit, end_time: e.target.value })}
                  className="block h-8 rounded-lg border border-neutral-300 px-2 text-xs bg-white"
                  data-testid={`daydetail-unavail-edit-endtime-${u.id}`} />
              </label>
            </>
          )}
          <div className="flex items-end gap-1" data-testid={`daydetail-unavail-edit-colors-${u.id}`}>
            <span className="text-[10px] font-semibold text-neutral-600 pb-1.5 mr-0.5">Color</span>
            {UNAVAIL_COLORS.map((c) => (
              <button key={c.key} type="button" title={c.label}
                onClick={() => setEdit({ ...edit, color: c.key })}
                data-testid={`daydetail-unavail-edit-color-${u.id}-${c.key}`}
                className={"h-7 w-7 rounded-lg transition-transform " + c.swatch +
                  ((edit.color || "gris") === c.key ? " ring-2 ring-offset-1 ring-neutral-800 scale-105" : " opacity-80 hover:opacity-100")} />
            ))}
          </div>
          <div className="flex gap-1 ml-auto">
            <button type="button" onClick={() => saveEdit("one")} disabled={!!busy}
              className="h-8 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-[11px] font-bold px-2.5 disabled:opacity-50"
              data-testid={`daydetail-unavail-edit-save-${u.id}`}>
              Guardar
            </button>
            {u.series_id && (
              <button type="button" onClick={() => saveEdit("series")} disabled={!!busy}
                title="Aplica motivo, franja y color a todos los días de esta repetición"
                className="h-8 rounded-lg bg-neutral-800 hover:bg-neutral-900 text-white text-[11px] font-bold px-2.5 disabled:opacity-50"
                data-testid={`daydetail-unavail-edit-save-series-${u.id}`}>
                Guardar serie
              </button>
            )}
            <button type="button" onClick={() => setEditing(false)}
              className="h-8 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-100 text-[11px] font-bold px-2.5"
              data-testid={`daydetail-unavail-edit-cancel-${u.id}`}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// V1.48.2 · Ventana rápida del día sobre el calendario: al hacer clic en una
// celda muestra un panel flotante (≈2 celdas de ancho, alto según nº de citas)
// con SOLO nombre y apellidos + R/P/Rest, sin desplazarse al detalle inferior.
function DayPeekOverlay({ peek, items, unavailabilities, metricsById, onClose, onNew, onKai, onWhatsappNext, onReminder, onDuplicate, onModify, onDelete, onStudentClick, onConfirm, onAttended, onCancel, onAvailability }) {
  // V1.92 · Al cancelar una clase práctica desde aquí hay que elegir si resta.
  const [cancelFor, setCancelFor] = useState(null);
  if (!peek) return null;
  const timeline = [
    ...(unavailabilities || []).map((u) => ({ kind: "unavail", time: u.all_day ? "00:00" : (u.time || "00:00"), u })),
    ...(items || []).map((a) => ({ kind: "appt", time: a.time || "00:00", a })),
  ].sort((x, y) => x.time.localeCompare(y.time));
  return (
    <div
      data-testid="day-peek-overlay"
      className="absolute z-20 bg-white rounded-xl border border-neutral-200 shadow-xl overflow-hidden flex flex-col"
      style={{ left: peek.left, top: peek.top, width: peek.width, maxHeight: peek.maxHeight }}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 bg-neutral-50/80 shrink-0">
        <div className="text-xs font-bold capitalize text-neutral-700 truncate">
          {formatDateES(peek.iso)}
          <span className="ml-1.5 text-neutral-400 font-normal">({timeline.length})</span>
        </div>
        <button
          type="button"
          data-testid="day-peek-close"
          onClick={onClose}
          className="text-neutral-400 hover:text-neutral-700 text-sm font-bold px-1 leading-none"
          title="Cerrar"
        >
          ✕
        </button>
      </div>
      <div className="overflow-y-auto divide-y divide-neutral-100 flex-1 min-h-0">
        {timeline.length === 0 && (
          <div className="px-3 py-4 text-xs text-neutral-400 text-center">Sin sesiones este día</div>
        )}
        {timeline.map((entry) => {
          if (entry.kind === "unavail") {
            const u = entry.u;
            return (
              <div key={u.id} className={"px-3 py-2 " + (unavailColor(u.color).key === "gris" ? "bg-neutral-100" : unavailColor(u.color).chip)} data-testid={`day-peek-unavail-${u.id}`} data-color={u.color || "gris"}>
                <div className="flex items-start gap-1.5 text-xs">
                  <ShieldOff className="h-3 w-3 shrink-0 mt-0.5" />
                  <span className="font-mono tabular-nums font-semibold shrink-0">
                    {u.all_day ? "Todo el día" : `${u.time}–${u.end_time || ""}`}
                  </span>
                  <span className="italic break-words min-w-0">{u.title || "No disponible"}</span>
                </div>
              </div>
            );
          }
          const a = entry.a;
          const m = metricsById?.[a.student_id];
          const isExam = isExamAppointment(a.appointment_type);
          const { numClasses } = isExam ? { numClasses: 0 } : computeClassBreakdown(a.time, a.end_time);
          return (
            <div key={a.id} className="px-3 py-2" data-testid={`day-peek-appt-${a.id}`}>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  data-testid={`day-peek-student-${a.id}`}
                  onClick={() => onStudentClick?.(a.student)}
                  title="Ver sus horarios"
                  className="font-bold text-[12px] text-neutral-800 uppercase break-words leading-tight min-w-0 text-left hover:text-orange-700 hover:underline decoration-dotted underline-offset-2"
                >
                  {studentCalendarLabelWide(a.student) || a.student?.name || "—"}
                </button>
                {m && !isExam && (
                  <div className="flex items-center gap-1 text-[10px] font-mono tabular-nums shrink-0">
                    <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 text-emerald-700 px-1.5 py-0.5">
                      <span className="opacity-60">R:</span><span className="font-semibold">{m.F}</span>
                    </span>
                    <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-50 text-amber-700 px-1.5 py-0.5">
                      <span className="opacity-60">P:</span><span className="font-semibold">{m.P}</span>
                    </span>
                    <span className={
                      "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 " +
                      (m.R <= 0 ? "bg-red-100 text-red-800" : m.R <= 2 ? "bg-amber-100 text-amber-800" : "bg-orange-100 text-orange-800")
                    }>
                      <span className="opacity-70">Rest:</span><span className="font-bold">{m.R}</span>
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-start flex-wrap gap-x-1 gap-y-0.5 text-[11px] text-neutral-500 mt-0.5">
                <Clock className="h-3 w-3 shrink-0 mt-0.5" />
                <span className="font-mono tabular-nums">
                  {a.time}{a.end_time ? `–${a.end_time}` : ""}
                </span>
                {numClasses > 0 && <span className="text-orange-600 font-semibold">({numClasses})</span>}
                {(a.pickup_location || a.pickup_location_end || a.circuit) && (
                  <>
                    <MapPin className="h-3 w-3 shrink-0 ml-0.5 mt-0.5" />
                    <span className="break-words min-w-0" data-testid={`day-peek-route-${a.id}`}>
                      {appointmentRouteLabel(a)}
                    </span>
                  </>
                )}
                {isExam && <span className={examTextClass(a.appointment_type) + " font-semibold uppercase ml-0.5"}>Examen {a.appointment_type === "examen_teorico" ? "teórico" : "práctico"}</span>}
              </div>
              {/* V1.88 · Atajos de WhatsApp: Px = próximas citas · R = recordatorio. */}
              <div className="flex items-center gap-1.5 mt-1">
                <button
                  type="button"
                  data-testid={`day-peek-wa-next-${a.id}`}
                  onClick={() => onWhatsappNext?.(a)}
                  title="WhatsApp · próximas citas del alumno"
                  className="inline-flex items-center gap-1 h-6 px-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-bold transition-colors"
                >
                  <MessageCircle className="h-3 w-3" /> Px
                </button>
                <button
                  type="button"
                  data-testid={`day-peek-wa-reminder-${a.id}`}
                  onClick={() => onReminder?.(a)}
                  title="WhatsApp · recordatorio de esta cita"
                  className="inline-flex items-center gap-1 h-6 px-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-[10px] font-bold transition-colors"
                >
                  <MessageCircle className="h-3 w-3" /> R
                </button>
                <button
                  type="button"
                  data-testid={`day-peek-confirm-${a.id}`}
                  onClick={() => onConfirm?.(a)}
                  title="Marcar como confirmada"
                  className={"inline-flex items-center justify-center h-6 w-6 rounded-md transition-colors " +
                    (a.status === "confirmed"
                      ? "bg-emerald-500 text-white"
                      : "bg-emerald-100 hover:bg-emerald-200 text-emerald-700")}
                >
                  <Check className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  data-testid={`day-peek-attended-${a.id}`}
                  onClick={() => onAttended?.(a)}
                  title={a.attended ? "Quitar asistencia" : "Marcar asistida"}
                  className={"inline-flex items-center justify-center h-6 w-6 rounded-md transition-colors " +
                    (a.attended
                      ? "bg-blue-500 text-white"
                      : "bg-blue-100 hover:bg-blue-200 text-blue-700")}
                >
                  <CircleCheck className="h-3 w-3" />
                </button>
                {cancelFor === a.id ? (
                  <>
                    <button
                      type="button"
                      data-testid={`day-peek-cancel-no-resta-${a.id}`}
                      onClick={() => { setCancelFor(null); onCancel?.(a, false); }}
                      className="h-6 px-1.5 rounded-md bg-white border border-emerald-300 text-emerald-800 text-[10px] font-bold hover:bg-emerald-50"
                    >
                      NO resta
                    </button>
                    <button
                      type="button"
                      data-testid={`day-peek-cancel-si-resta-${a.id}`}
                      onClick={() => { setCancelFor(null); onCancel?.(a, true); }}
                      className="h-6 px-1.5 rounded-md bg-white border border-red-300 text-red-700 text-[10px] font-bold hover:bg-red-100"
                    >
                      SÍ resta
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    data-testid={`day-peek-cancel-${a.id}`}
                    onClick={() => (isExam ? onCancel?.(a, false) : setCancelFor(a.id))}
                    title="Cancelar esta cita"
                    className={"inline-flex items-center justify-center h-6 w-6 rounded-md transition-colors " +
                      (a.status === "cancelled"
                        ? "bg-red-500 text-white"
                        : "bg-red-100 hover:bg-red-200 text-red-700")}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`day-peek-availability-${a.id}`}
                  onClick={() => onAvailability?.(a.student)}
                  title="Ver su horario disponible e incidencias"
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                >
                  <CalendarClock className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  data-testid={`day-peek-modify-${a.id}`}
                  onClick={() => onModify?.(a)}
                  title="Modificar esta cita"
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                >
                  <Pencil className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  data-testid={`day-peek-delete-${a.id}`}
                  onClick={() => onDelete?.(a)}
                  title="Eliminar esta cita"
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-red-100 hover:bg-red-200 text-red-700 transition-colors"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  data-testid={`day-peek-duplicate-${a.id}`}
                  onClick={() => onDuplicate?.(a)}
                  title="Duplicar esta cita (abre Nueva cita con los mismos datos)"
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-neutral-200 hover:bg-neutral-300 text-neutral-700 transition-colors"
                >
                  <Copy className="h-3 w-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 py-2 border-t border-neutral-100 bg-neutral-50/60 shrink-0 flex gap-1.5">
        <button
          type="button"
          onClick={() => onNew?.(peek.iso)}
          data-testid="day-peek-new-appt"
          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white h-8 text-xs font-bold transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Nueva cita
        </button>
        <button
          type="button"
          onClick={() => onKai?.()}
          data-testid="day-peek-kai-planner"
          title="Kai Planificador"
          className="inline-flex items-center justify-center rounded-lg bg-neutral-900 hover:bg-neutral-800 text-orange-400 h-8 w-9 transition-colors"
        >
          <Sparkles className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function DayDetail({ selectedDate, items, unavailabilities, teachers, loading, metricsById, actions, onNewOnThisDate, onPreview, onReload, onAcceptRequest, onModifyAndConfirm, onCounterPropose, onQuickModify, onCancelChangeRequest, onPrevDay, onNextDay }) {
  // V1.35 · Envío agrupado de próximas citas del alumno seleccionado
  const [bulkForStudent, setBulkForStudent] = useState(null);
  // V1.38 · Diálogo No disponible del profesor
  const [unavailTeacher, setUnavailTeacher] = useState("");
  const [pickedTeacher, setPickedTeacher] = useState("");
  const dayUnavail = unavailabilities || [];
  // V1.48.1 · Timeline unificado: bloqueos como una cita más, en orden
  // cronológico real (día completo primero, a las 00:00).
  const timeline = [
    ...dayUnavail.map((u) => ({ kind: "unavail", time: u.all_day ? "00:00" : (u.time || "00:00"), u })),
    ...items.map((a) => ({ kind: "appt", time: a.time || "00:00", a })),
  ].sort((x, y) => x.time.localeCompare(y.time));
  // V1.41 · Ahora `teachers` viene como [{code,label,legacy}] alimentado
  // desde `/teachers` (tabla Profesorado). Priorizamos canónicos (no legacy).
  const teachersList = (() => {
    const list = (teachers || []).filter((t) => !t.legacy);
    return preferJoseAlfredo(list.length > 0 ? list : (teachers || []));
  })();
  const fallback = { code: "JA", label: "JA" };
  const currentTeacher = pickedTeacher || teachersList[0]?.code || fallback.code;
  return (
    <div className="card-surface p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          {onPrevDay && (
            <button type="button" onClick={onPrevDay} data-testid="daydetail-prev" aria-label="Día anterior"
              className="btn-secondary !h-8 !px-2.5">
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <div className="font-bold capitalize">{formatDateES(selectedDate)}</div>
          {onNextDay && (
            <button type="button" onClick={onNextDay} data-testid="daydetail-next" aria-label="Día siguiente"
              className="btn-secondary !h-8 !px-2.5">
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-neutral-500">
            {items.length} {items.length === 1 ? "sesión" : "sesiones"}
          </div>
          <button
            type="button"
            onClick={() => onNewOnThisDate(selectedDate)}
            data-testid="cal-daydetail-new"
            className="btn-secondary !h-8 !px-3 !text-xs"
          >
            <Plus className="h-3.5 w-3.5" /> Nueva sesión
          </button>
          <button
            data-testid="daydetail-teacher-unavail"
            onClick={() => setUnavailTeacher(currentTeacher)}
            className="btn-secondary !h-9 !px-3 !text-xs"
            title="Marcar No disponible del profesor"
          >
            <ShieldOff className="h-3.5 w-3.5" /> No disponible
          </button>
          {teachersList.length > 1 && (
            <select
              value={currentTeacher}
              onChange={(e) => setPickedTeacher(e.target.value)}
              data-testid="daydetail-teacher-picker"
              className="h-9 rounded-lg border border-neutral-300 bg-white px-2 text-xs"
              title="Profesor para el bloqueo"
            >
              {teachersList.map((t) => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-neutral-500 py-4">Cargando…</div>
      ) : items.length === 0 && dayUnavail.length === 0 ? (
        <div className="text-sm text-neutral-500 py-4">No hay sesiones este día.</div>
      ) : (
        <div className="space-y-3">
          {timeline.map((entry) => {
            if (entry.kind === "unavail") {
              return (
                <UnavailabilityCard key={entry.u.id} u={entry.u} onReload={onReload} />
              );
            }
            const a = entry.a;
            const isShared = (a.shared_students || []).length > 0;
            const isExam = isExamAppointment(a.appointment_type);
            const { numClasses } = isExam
              ? { numClasses: 0 }
              : computeClassBreakdown(a.time, a.end_time);
            const m = metricsById?.[a.student_id];
            return (
              <div
                key={a.id}
                data-testid={`cal-appt-${a.id}`}
                className="border border-neutral-100 rounded-xl px-3 py-2.5 space-y-2"
              >
                {/* V1.38.2 · Chips de estado como en la vista Alumnos → Citas. */}
                <StatusBadges appt={a} />
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className={"font-semibold text-sm" + (isExam ? " " + examTextClass(a.appointment_type) : "")}>
                      {studentCalendarLabelWide(a.student) || a.student?.name || "—"}
                      {isExam && (
                        <span className={"ml-2 inline-flex items-center rounded-full text-[10px] font-bold uppercase px-2 py-0.5 align-middle " + examBadgeClass(a.appointment_type)}>
                          Examen {a.appointment_type === "examen_teorico" ? "teórico" : "práctico"}
                        </span>
                      )}
                      {isShared && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold px-2 py-0.5 align-middle">
                          +{a.shared_students.length} compart.
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-500 flex items-center gap-3 flex-wrap mt-0.5">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {a.time}{a.end_time ? `–${a.end_time}` : ""}
                        {numClasses > 0 && (
                          <span className="text-orange-600 font-semibold ml-1">({numClasses})</span>
                        )}
                      </span>
                      <span className="flex items-center gap-1" data-testid={`cal-appt-route-${a.id}`}>
                        <MapPin className="h-3 w-3" /> {appointmentRouteLabel(a)}
                      </span>
                    </div>
                    {a.status === "change_requested" && (a.requested_date || a.requested_time) && (
                      <div
                        data-testid={`cal-change-request-notice-${a.id}`}
                        className="mt-2 rounded-lg bg-blue-50 border border-blue-200 px-2.5 py-2 text-[11px] text-blue-900"
                      >
                        <div>
                          <span className="font-semibold">
                            {a.proposed_by === "teacher" ? "Tú (profesor) has propuesto:" : "El alumno propone:"}
                          </span>{" "}
                          <span className="font-mono">
                            {a.requested_date || "?"} · {a.requested_time || "?"}
                            {a.requested_end_time ? `–${a.requested_end_time}` : ""}
                          </span>
                        </div>
                        {a.proposed_by !== "teacher" && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <button
                              type="button"
                              onClick={() => onAcceptRequest?.(a)}
                              data-testid={`cal-accept-request-${a.id}`}
                              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-[10px] font-semibold px-2 py-1 hover:bg-emerald-700"
                            >
                              Aceptar propuesta
                            </button>
                            <button
                              type="button"
                              onClick={() => onModifyAndConfirm?.(a)}
                              data-testid={`cal-modify-confirm-${a.id}`}
                              className="inline-flex items-center gap-1 rounded-md bg-white border border-blue-300 text-blue-800 text-[10px] font-semibold px-2 py-1 hover:bg-blue-100"
                            >
                              Modificar y confirmar
                            </button>
                            <button
                              type="button"
                              onClick={() => onCounterPropose?.(a)}
                              data-testid={`cal-counter-propose-${a.id}`}
                              className="inline-flex items-center gap-1 rounded-md bg-orange-500 text-white text-[10px] font-semibold px-2 py-1 hover:bg-orange-600"
                            >
                              Contra-proponer
                            </button>
                          </div>
                        )}
                        {a.proposed_by === "teacher" && (
                          <>
                            <div className="text-[10px] text-blue-700 mt-1">Esperando respuesta del alumno vía WhatsApp…</div>
                            {/* V1.42.17 · Vías de escape cuando el profesor
                                propuso el cambio: el alumno lo confirmó por
                                otra vía, cambió de idea, o no le llegó el
                                WhatsApp. Todas con confirm() previo. */}
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const msg = `¿Confirmar la nueva cita en NOMBRE DEL ALUMNO?\n\n${a.requested_date || ""} · ${a.requested_time || ""}${a.requested_end_time ? "–" + a.requested_end_time : ""}\n\nÚsalo sólo si ya has hablado con el alumno y ha aceptado verbalmente.`;
                                  if (!window.confirm(msg)) return;
                                  onAcceptRequest?.(a);
                                }}
                                data-testid={`cal-confirm-on-behalf-${a.id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-[10px] font-semibold px-2 py-1 hover:bg-emerald-700"
                                title="Confirmar en nombre del alumno (cuando ya ha aceptado por otra vía)"
                              >
                                Confirmar (en nombre del alumno)
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!window.confirm("¿Cancelar la propuesta y dejar la cita en su horario ORIGINAL?\n\nSe descarta el cambio propuesto y la cita vuelve a estar confirmada tal cual estaba.")) return;
                                  onCancelChangeRequest?.(a);
                                }}
                                data-testid={`cal-cancel-change-request-${a.id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-white border border-neutral-300 text-neutral-700 text-[10px] font-semibold px-2 py-1 hover:bg-neutral-100"
                                title="Revertir la propuesta — la cita vuelve a su horario original"
                              >
                                Cancelar propuesta
                              </button>
                              <button
                                type="button"
                                onClick={() => onPreview?.(a)}
                                data-testid={`cal-resend-whatsapp-${a.id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-green-600 text-white text-[10px] font-semibold px-2 py-1 hover:bg-green-700"
                                title="Reenviar el enlace de confirmación al alumno por WhatsApp"
                              >
                                Reenviar WhatsApp
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {m && (
                    <div
                      data-testid={`cal-appt-metrics-${a.id}`}
                      className="flex items-center gap-2 sm:gap-2.5 flex-wrap text-[10px] font-mono tabular-nums shrink-0"
                      title={`Contratadas: ${m.T} · Realizadas: ${m.F} · Programadas: ${m.P} · No asistidas: ${m.M} · Ajustes: ${m.AJ >= 0 ? "+" : ""}${m.AJ} · Restantes: ${m.R}`}
                    >
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
                        <span className="opacity-60">C:</span><span className="font-semibold">{m.T}</span>
                      </span>
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-50 text-emerald-700 px-1.5 py-0.5">
                        <span className="opacity-60">R:</span><span className="font-semibold">{m.F}</span>
                      </span>
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-50 text-amber-700 px-1.5 py-0.5">
                        <span className="opacity-60">P:</span><span className="font-semibold">{m.P}</span>
                      </span>
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-red-50 text-red-700 px-1.5 py-0.5">
                        <span className="opacity-60">NA:</span><span className="font-semibold">{m.M}</span>
                      </span>
                      <span className="inline-flex items-center gap-0.5 rounded-md bg-blue-50 text-blue-700 px-1.5 py-0.5">
                        <span className="opacity-60">A:</span><span className="font-semibold">{m.AJ >= 0 ? `+${m.AJ}` : m.AJ}</span>
                      </span>
                      <span className={
                        "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 " +
                        (m.R <= 0
                          ? "bg-red-100 text-red-800"
                          : m.R <= 2
                            ? "bg-amber-100 text-amber-800"
                            : "bg-orange-100 text-orange-800")
                      }>
                        <span className="opacity-70">Rest:</span><span className="font-bold">{m.R}</span>
                      </span>
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {isShared ? (
                      <button
                        data-testid={`cal-preview-${a.id}`}
                        onClick={() => onPreview(a)}
                        className="btn-whatsapp !h-9 !px-3 !text-xs"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Ver mensajes
                      </button>
                    ) : (
                      <button
                        data-testid={`cal-whatsapp-${a.id}`}
                        onClick={() => actions.sendWhatsapp(a)}
                        className="btn-whatsapp !h-9 !px-3 !text-xs"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                        WhatsApp
                      </button>
                    )}
                    <button
                      data-testid={`cal-reminder-${a.id}`}
                      onClick={() => actions.sendReminder(a)}
                      className="btn-secondary !h-9 !px-3 !text-xs"
                    >
                      <Bell className="h-3.5 w-3.5" />
                      Recordar
                    </button>
                    {/* V1.35 · Enviar en un enlace todas las próximas citas
                        del alumno (misma UX individual, N tarjetas). */}
                    <button
                      data-testid={`cal-bulk-whatsapp-${a.id}`}
                      onClick={() => setBulkForStudent({
                        id: a.student?.id || a.student_id,
                        name: a.student?.name || "",
                        phone: a.student?.phone || "",
                      })}
                      className="btn-secondary !h-9 !px-3 !text-xs"
                      title="Enviar próximas sesiones del alumno"
                    >
                      <Send className="h-3.5 w-3.5" />
                      Próximas
                    </button>
                  </div>
                </div>
                {/* V1.38.2 · Acciones (Confirmar / Cancelar / Asistió / Modificar / Observaciones / Borrar). */}
                <AppointmentActions
                  appt={a}
                  onConfirm={() => actions.setStatus(a, "confirmed")}
                  onReject={(consumes) => actions.setStatus(a, "cancelled", { cancel_consumes: !!consumes })}
                  onSetCancelConsumes={(v) => actions.setCancelConsumes(a, v)}
                  onToggleAttended={() => actions.toggleAttended(a)}
                  onDelete={() => actions.removeAppt(a)}
                  onModify={() => onQuickModify?.(a)}
                  onSaveNotes={async (notes) => {
                    try {
                      await api.patch(`/appointments/${a.id}`, { notes });
                      onReload?.();
                    } catch (err) {
                      // silencioso: el hook global ya muestra el error si aplica
                    }
                  }}
                  onReload={onReload}
                />
              </div>
            );
          })}
        </div>
      )}

      <BulkWhatsAppDialog
        open={!!bulkForStudent}
        onClose={() => setBulkForStudent(null)}
        studentId={bulkForStudent?.id || ""}
        studentName={bulkForStudent?.name || ""}
        phone={bulkForStudent?.phone || ""}
      />

      <TeacherUnavailabilityDialog
        open={!!unavailTeacher}
        onClose={() => setUnavailTeacher("")}
        teacher={unavailTeacher}
        initialDate={selectedDate}
        onChanged={onReload}
      />
    </div>
  );
}

// V1.53 · Filtro multi-alumno con checkboxes: muestra en el calendario solo
// las clases de los alumnos marcados (p. ej. Pau, Bruce y Cristina).
function StudentFilterDropdown({ students, value, onChange }) {
  const [query, setQuery] = useState("");
  const label = (s) => studentShortLabel(s) || "—";
  const sorted = [...students].sort((a, b) => label(a).localeCompare(label(b)));
  const visible = query
    ? sorted.filter((s) => label(s).toLowerCase().includes(query.toLowerCase()))
    : sorted;
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="cal-filter-students"
          className={
            "inline-flex items-center gap-1.5 rounded-xl border px-3 h-10 text-sm transition-colors " +
            (value.length > 0
              ? "border-orange-400 bg-orange-50 text-orange-800 font-semibold"
              : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50")
          }
          title="Filtrar por alumnos"
        >
          <Users2 className="h-4 w-4" />
          {value.length > 0 ? `${value.length} alumno${value.length > 1 ? "s" : ""}` : "Todos los alumnos"}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="p-2 border-b border-neutral-100">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar alumno…"
            data-testid="cal-filter-students-search"
            className="w-full rounded-lg border border-neutral-300 px-2 h-8 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {visible.length === 0 && (
            <p className="px-3 py-2 text-xs text-neutral-400 italic">Sin coincidencias.</p>
          )}
          {visible.map((s) => (
            <label
              key={s.id}
              data-testid={`cal-filter-student-${s.id}`}
              className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-neutral-50"
            >
              <input
                type="checkbox"
                checked={value.includes(s.id)}
                onChange={() => toggle(s.id)}
                className="h-4 w-4 accent-orange-500 shrink-0"
              />
              <span className="truncate font-medium text-neutral-800">{label(s)}</span>
            </label>
          ))}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t border-neutral-100">
            <button
              type="button"
              onClick={() => onChange([])}
              data-testid="cal-filter-students-clear"
              className="w-full rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 h-8 text-xs font-semibold"
            >
              Limpiar filtro
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


export default function CalendarTab() {
  const todayDate = useMemo(() => new Date(), []);
  const [view, setView] = useState("month"); // month | week | biweek
  const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));
  const [rangeCursor, setRangeCursor] = useState(() => startOfWeek(new Date()));
  const [appointments, setAppointments] = useState([]);
  const [unavailabilities, setUnavailabilities] = useState([]); // V1.38 · bloqueos No disponible
  const [cicloMilestones, setCicloMilestones] = useState([]); // V1.50 · hitos Ciclo DGT
  const [teachers, setTeachers] = useState([]); // V1.38 · lista de códigos de profesor
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => isoFromDate(new Date()));
  // V1.48.2 · Ventana rápida flotante sobre el calendario (click en celda)
  const [peek, setPeek] = useState(null);
  const monthWrapRef = useRef(null);
  const handleMonthSelect = useCallback((iso, e) => {
    setSelectedDate(iso);
    const wrapEl = monthWrapRef.current;
    const cellEl = e?.currentTarget;
    if (!wrapEl || !cellEl) return;
    const wrap = wrapEl.getBoundingClientRect();
    const cell = cellEl.getBoundingClientRect();
    const colW = wrap.width / 7;
    // V1.90 · Ventana más ancha y que aprovecha la pantalla: antes se limitaba
    // al alto que quedaba dentro del mes, así que en las últimas semanas salía
    // diminuta aunque hubiera sitio de sobra.
    const panelW = Math.min(wrap.width - 8, Math.max(380, colW * 2.6));
    const cellLeft = cell.left - wrap.left;
    let left = cellLeft + cell.width + 6;
    if (left + panelW > wrap.width - 4) left = cellLeft - panelW - 6;
    if (left < 4) left = Math.max(4, Math.min(wrap.width - panelW - 4, cellLeft));
    let top = cell.top - wrap.top;
    const desired = Math.min(620, Math.round(window.innerHeight * 0.72));
    const spaceBelow = window.innerHeight - cell.top - 16;
    let maxHeight = Math.max(260, Math.min(desired, spaceBelow));
    if (maxHeight < desired) {
      // Sube el panel para ganar alto cuando la celda está abajo del todo.
      const shift = Math.min(Math.max(top, 0), desired - maxHeight);
      top -= shift;
      maxHeight += shift;
    }
    setPeek({ iso, left, top, width: panelW, maxHeight });
  }, []);
  const [creating, setCreating] = useState(null); // null | {} | { date }
  const [kaiOpen, setKaiOpen] = useState(false); // V1.60 · Kai Planificador multi-alumno
  const [previewAppt, setPreviewAppt] = useState(null);
  const [metricsById, setMetricsById] = useState({});
  const [filter, setFilter] = useState("all"); // V1.38.2 · filtro global del calendario
  const [teacherFilter, setTeacherFilter] = useState(""); // V1.38.5 · filtro por profesor (código)
  const [studentFilter, setStudentFilter] = useState([]); // V1.53 · filtro multi-alumno (checkboxes)
  const [counterFor, setCounterFor] = useState(null); // V1.38.3 · cita con contra-propuesta abierta
  const [editingQuick, setEditingQuick] = useState(null); // V1.38.4 · cita en edición rápida
  // V1.88 · WhatsApp "Px" desde la ventana rápida del día.
  const [peekBulkStudent, setPeekBulkStudent] = useState(null);
  // V1.91 · Ficha del alumno abierta en Horarios desde el calendario.
  const [scheduleStudent, setScheduleStudent] = useState(null);
  // V1.93 · Vista rápida de solo el horario disponible + incidencias.
  const [availStudent, setAvailStudent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // V1.38 · Ventana amplia de unavailabilities (hoy → +90 días) para cubrir
      // vista mensual sin filtrar por profesor (matriz + profesores en igualdad).
      const today = todayISO();
      const in90 = (() => {
        const d = new Date(today); d.setDate(d.getDate() + 90); return isoFromDate(d);
      })();
      const [
        { data: appts },
        { data: studs },
        { data: metrics },
        { data: unavailData },
        teachersResp,
        cicloResp,
      ] = await Promise.all([
        api.get("/appointments"),
        api.get("/students"),
        api.get("/students/practice-metrics", { params: { active_only: false } }),
        api.get("/teacher-unavailability", { params: { date_from: today, date_to: in90 } }),
        api.get("/teachers", { params: { include_inactive: false } }).catch(() => ({ data: [] })),
        api.get("/ciclo-milestones").catch(() => ({ data: [] })),
      ]);
      setAppointments(appts);
      setStudents(studs);
      setCicloMilestones(cicloResp.data || []);
      const items = Array.isArray(unavailData?.items) ? unavailData.items : [];
      // V1.42.14 · Expandimos las vacaciones/ausencias del profesor
      // (`teacher.vacations` de la ficha) a bloqueos sintéticos "día completo"
      // (all_day=true) para que el calendario los pinte en gris igual que
      // los "No disponible" marcados desde el botón del calendario.
      // No se persisten en Mongo — se generan en cada carga.
      const syntheticFromVacations = [];
      for (const t of (teachersResp.data || [])) {
        if (t.active === false) continue;
        const name = (t.name || "").trim();
        const parts = name.split(/\s+/).filter(Boolean);
        let code = "";
        if (parts.length >= 2) code = (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
        else if (parts.length === 1) code = parts[0].slice(0, 2).toUpperCase();
        if (!code) continue;
        for (const v of (t.vacations || [])) {
          const ds = v.date_start, de = v.date_end || v.date_start;
          if (!ds) continue;
          const d0 = new Date(ds + "T00:00:00");
          const d1 = new Date((de || ds) + "T00:00:00");
          if (Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) continue;
          const cur = new Date(d0);
          let safety = 400;
          // V1.42.14 bugfix (V1.42.16) · Ojo con toISOString(): devuelve UTC y
          // en Europe/Madrid (+02:00 verano / +01:00 invierno) el día `17 00:00`
          // local es `16 22:00` UTC → el slice(0,10) daba `2026-08-16`
          // (todos los días retrocedidos 1). Construimos el ISO en local.
          const isoLocal = (dd) => `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}-${String(dd.getDate()).padStart(2, "0")}`;
          while (cur <= d1 && safety-- > 0) {
            const iso = isoLocal(cur);
            syntheticFromVacations.push({
              id: `synth-vac-${t.id}-${iso}`,
              teacher: code,
              title: v.reason || "Vacaciones",
              date: iso,
              time: "00:00",
              end_time: "23:59",
              all_day: true,
              source: "teacher.vacations",
              notes: "",
            });
            cur.setDate(cur.getDate() + 1);
          }
        }
      }
      setUnavailabilities([...items, ...syntheticFromVacations]);
      // V1.41 · Fuente única = tabla de Profesorado (/teachers). Alimentamos
      // el desplegable, el filtro y el chip de ocupación con los profesores
      // reales. Los códigos huérfanos que aparezcan solo en el histórico
      // (ej. tipos legacy antes de existir Gaby) se mantienen pero marcados
      // como "legacy" para no perder acceso a sesiones/bloqueos antiguos.
      const canonical = (teachersResp.data || [])
        .filter((t) => t.active !== false)
        .map((t) => {
          const name = (t.name || "").trim();
          const parts = name.split(/\s+/).filter(Boolean);
          let code = "";
          if (parts.length >= 2) code = (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
          else if (parts.length === 1) code = parts[0].slice(0, 2).toUpperCase();
          return { code, label: name || code, legacy: false };
        })
        .filter((o) => o.code);
      const canonicalCodes = new Set(canonical.map((o) => o.code));
      const legacy = new Set();
      for (const a of appts || []) {
        const t = (a.teacher || "").trim();
        if (t && !canonicalCodes.has(t)) legacy.add(t);
      }
      for (const u of items) {
        const t = (u.teacher || "").trim();
        if (t && !canonicalCodes.has(t)) legacy.add(t);
      }
      const legacyList = Array.from(legacy)
        .sort()
        .map((code) => ({ code, label: `${code} · legacy`, legacy: true }));
      setTeachers([...canonical, ...legacyList]);
      const map = {};
      for (const s of metrics || []) map[s.id] = s;
      setMetricsById(map);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useDataRefresh(load);

  const actions = useAppointmentActions(load);

  // V1.38.2 · Aplica el filtro global antes de agrupar por fecha, así el grid
  // (mes / semana / quincena) y el panel del día muestran únicamente las
  // citas que cumplen el filtro seleccionado.
  // V1.38.5 · Añadimos también filtrado por profesor (código como "JA").
  const filteredAppointments = useMemo(() => {
    let list = applyFilter(appointments, filter);
    if (teacherFilter) list = list.filter((a) => (a.teacher || "") === teacherFilter);
    if (studentFilter.length > 0) {
      list = list.filter((a) =>
        studentFilter.includes(a.student_id) ||
        (a.shared_student_ids || []).some((id) => studentFilter.includes(id))
      );
    }
    return list;
  }, [appointments, filter, teacherFilter, studentFilter]);

  const filteredUnavailabilities = useMemo(() => {
    if (!teacherFilter) return unavailabilities;
    return unavailabilities.filter((u) => (u.teacher || "") === teacherFilter);
  }, [unavailabilities, teacherFilter]);

  const byDate = useMemo(() => {
    const m = {};
    for (const a of filteredAppointments) {
      if (!m[a.date]) m[a.date] = [];
      m[a.date].push(a);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.time.localeCompare(b.time));
    return m;
  }, [filteredAppointments]);

  // V1.38 · Índice por fecha de los bloqueos No disponible. Respetan también
  // el filtro por profesor (si hay uno activo) para no saturar el grid.
  const unavailByDate = useMemo(() => {
    const m = {};
    for (const u of filteredUnavailabilities) {
      if (!u.date) continue;
      if (!m[u.date]) m[u.date] = [];
      m[u.date].push(u);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return m;
  }, [filteredUnavailabilities]);

  // V1.50 · Hitos Ciclo DGT agrupados por fecha (evento rojo a las 07:00).
  const milestonesByDate = useMemo(() => {
    const m = {};
    for (const it of cicloMilestones) {
      if (!it.date) continue;
      if (!m[it.date]) m[it.date] = [];
      m[it.date].push(it);
    }
    return m;
  }, [cicloMilestones]);

  // V1.39.2 · Ocupación semanal por profesor: suma de minutos de las sesiones
  // (no canceladas) de la semana en curso (lunes-domingo). Se calcula sobre
  // TODOS los appointments cargados, sin depender del filtro global — así el
  // chip refleja la carga real aunque el usuario esté filtrando por profesor.
  const weekLoad = useMemo(() => {
    const monday = startOfWeek(todayDate);
    const start = isoFromDate(monday);
    const endDate = new Date(monday); endDate.setDate(monday.getDate() + 6);
    const end = isoFromDate(endDate);
    const map = new Map(); // code -> minutes
    for (const a of appointments) {
      if (!a?.date || a.date < start || a.date > end) continue;
      if (a.status === "cancelled") continue;
      const t = (a.teacher || "").trim();
      if (!t) continue;
      const parse = (s) => {
        const [h, m] = String(s || "").split(":").map(Number);
        return Number.isFinite(h) ? h * 60 + (m || 0) : NaN;
      };
      const s0 = parse(a.time);
      const e0 = parse(a.end_time || a.time);
      const dur = Math.max(0, (e0 || 0) - (s0 || 0));
      if (dur > 0) map.set(t, (map.get(t) || 0) + dur);
    }
    return Array.from(map.entries())
      .map(([code, min]) => {
        const t = (teachers || []).find((x) => x.code === code);
        return { code, min, hours: min / 60, label: t?.label || code, legacy: t?.legacy };
      })
      .sort((a, b) => b.min - a.min);
  }, [appointments, todayDate, teachers]);

  // Cierra la ventana rápida al cambiar de mes o de vista
  useEffect(() => { setPeek(null); }, [monthCursor, view]);

  const selectedList = useMemo(() => byDate[selectedDate] || [], [byDate, selectedDate]);
  const selectedUnavail = useMemo(() => unavailByDate[selectedDate] || [], [unavailByDate, selectedDate]);

  const prev = useCallback(() => {
    if (view === "month") setMonthCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
    else if (view === "day") {
      setSelectedDate((iso) => {
        const d = new Date(iso); d.setDate(d.getDate() - 1); return isoFromDate(d);
      });
    }
    else if (view === "week") setRangeCursor((c) => { const n = new Date(c); n.setDate(c.getDate() - 7); return n; });
    else setRangeCursor((c) => { const n = new Date(c); n.setDate(c.getDate() - 14); return n; });
  }, [view]);

  const next = useCallback(() => {
    if (view === "month") setMonthCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
    else if (view === "day") {
      setSelectedDate((iso) => {
        const d = new Date(iso); d.setDate(d.getDate() + 1); return isoFromDate(d);
      });
    }
    else if (view === "week") setRangeCursor((c) => { const n = new Date(c); n.setDate(c.getDate() + 7); return n; });
    else setRangeCursor((c) => { const n = new Date(c); n.setDate(c.getDate() + 14); return n; });
  }, [view]);

  const goToday = useCallback(() => {
    setMonthCursor(startOfMonth(todayDate));
    setRangeCursor(startOfWeek(todayDate));
    setSelectedDate(isoFromDate(todayDate));
    // V1.58 · HOY abre la vista de día: un único recuadro amplio con flechas
    setView("day");
  }, [todayDate]);

  const onViewChange = (v) => {
    setView(v);
    // Keep the selected date visible when switching views
    if (v !== "month") setRangeCursor(startOfWeek(new Date(selectedDate)));
    else setMonthCursor(startOfMonth(new Date(selectedDate)));
  };

  const openNew = (dateIso) => {
    setCreating({ date: dateIso || selectedDate });
  };

  const onSaved = async () => {
    // Si estábamos resolviendo una change_requested, limpiamos los
    // campos `requested_*` y marcamos la cita como confirmed en un
    // segundo PATCH (el AppointmentDialog no sabe de esta semántica).
    const wasResolvingChangeRequest = !!creating?._resolveChangeRequest;
    const appointmentId = creating?.id;
    setCreating(null);
    if (wasResolvingChangeRequest && appointmentId) {
      try {
        await api.patch(`/appointments/${appointmentId}`, {
          status: "confirmed",
          requested_date: "",
          requested_time: "",
          requested_end_time: "",
        });
        toast.success("Cita modificada y confirmada");
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Error al confirmar");
      }
    }
    load();
  };

  return (
    <div className="space-y-4">
      <CalendarToolbar
        view={view}
        onViewChange={onViewChange}
        onPrev={prev}
        onNext={next}
        onToday={goToday}
        onNew={() => openNew(selectedDate)}
        onKai={() => setKaiOpen(true)}
        onRefresh={load}
        refreshing={loading}
        label={rangeLabel(view, view === "month" ? monthCursor : (view === "day" ? new Date(selectedDate) : rangeCursor))}
      />
      <KaiPlannerDialog open={kaiOpen} onOpenChange={setKaiOpen} onChanged={load} teacher={teacherFilter} />

      {/* V1.38.2 · Filtros globales del calendario. Aplican al grid y al panel del día.
          V1.38.5 · Filtro por profesor (código) para acotar la agenda de un vistazo. */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <AppointmentFilters value={filter} onChange={setFilter} />
          <select
            data-testid="cal-filter-teacher"
            value={teacherFilter}
            onChange={(e) => setTeacherFilter(e.target.value)}
            className="rounded-xl border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            title="Filtrar por profesor"
          >
            <option value="">Todos los profesores</option>
            {teachers.map((t) => {
              const n = appointments.filter((a) => (a.teacher || "") === t.code).length;
              return (
                <option key={t.code} value={t.code}>{t.label} · {n}</option>
              );
            })}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <StudentFilterDropdown students={students} value={studentFilter} onChange={setStudentFilter} />
          <div className="text-xs text-neutral-500 tabular-nums">
            {filteredAppointments.length} de {appointments.length} citas
            {teacherFilter && <span className="ml-1 text-orange-700 font-semibold">· prof. {teacherFilter}</span>}
            {studentFilter.length > 0 && <span className="ml-1 text-orange-700 font-semibold">· {studentFilter.length} alumno(s)</span>}
          </div>
        </div>
      </div>

      {/* V1.39.2 · Ocupación semanal por profesor (mini contador de horas
          para detectar sobrecarga). Umbrales: >35h rojo, >25h ámbar. */}
      {weekLoad.length > 0 && (
        <div
          className="flex items-center gap-1.5 flex-wrap text-[11px] text-neutral-600"
          data-testid="cal-week-load"
        >
          <span className="font-semibold uppercase tracking-wider text-neutral-500">
            Ocupación semanal:
          </span>
          {weekLoad.map((w) => {
            const overload = w.hours >= 35;
            const busy = w.hours >= 25;
            const tone = overload
              ? "bg-red-50 border-red-300 text-red-800"
              : busy
                ? "bg-amber-50 border-amber-300 text-amber-800"
                : "bg-emerald-50 border-emerald-300 text-emerald-800";
            const active = teacherFilter === w.code;
            return (
              <button
                key={w.code}
                type="button"
                onClick={() => setTeacherFilter(active ? "" : w.code)}
                data-testid={`cal-week-load-${w.code}`}
                title={`${w.label} · ${w.hours.toFixed(1)}h esta semana · ${(w.min / 45).toFixed(1)} clases (45m)`}
                className={
                  "inline-flex items-center gap-1 rounded-full border px-2.5 h-6 font-mono tabular-nums font-semibold transition-colors " +
                  tone +
                  (active ? " ring-2 ring-orange-400" : "") +
                  (w.legacy ? " opacity-70 italic" : "")
                }
              >
                <span>{w.code}</span>
                <span className="opacity-70">·</span>
                <span>{w.hours.toFixed(1)}h</span>
              </button>
            );
          })}
        </div>
      )}

      {view === "month" && (
        <div ref={monthWrapRef} className="relative">
          <MonthGrid
            cursor={monthCursor}
            byDate={byDate}
            unavailByDate={unavailByDate}
            milestonesByDate={milestonesByDate}
            todayIso={todayISO()}
            selectedDate={selectedDate}
            onSelect={handleMonthSelect}
          />
          <DayPeekOverlay
            peek={peek}
            items={peek ? byDate[peek.iso] || [] : []}
            unavailabilities={peek ? unavailByDate[peek.iso] || [] : []}
            metricsById={metricsById}
            onWhatsappNext={(a) => setPeekBulkStudent({
              id: a.student?.id || a.student_id,
              name: a.student?.name || "",
              phone: a.student?.phone || "",
            })}
            onReminder={(a) => actions.sendReminder(a)}
            onStudentClick={(st) => { if (st?.id) setScheduleStudent(st); }}
            onAvailability={(st) => { if (st?.id) setAvailStudent(st); }}
            onConfirm={(a) => actions.setStatus(a, "confirmed")}
            onAttended={(a) => actions.toggleAttended(a)}
            onCancel={(a, consumes) => actions.setStatus(a, "cancelled", { cancel_consumes: !!consumes })}
            onModify={(a) => {
              setCreating({ ...a });
              setPeek(null);
            }}
            onDelete={async (a) => {
              await actions.removeAppt(a);
              setPeek(null);
            }}
            onDuplicate={(a) => {
              // V1.88 · Duplicar: abre Nueva cita con los mismos datos (sin id
              // para que se guarde como cita nueva) y así solo cambias la fecha.
              setCreating({ ...a, id: undefined, status: "pending", attended: null });
              setPeek(null);
            }}
            onClose={() => setPeek(null)}
            onNew={(iso) => {
              setCreating({ date: iso });
              setPeek(null);
            }}
            onKai={() => {
              setKaiOpen(true);
              setPeek(null);            }}
          />
        </div>
      )}
      {/* V1.58 · Vista "Hoy": el recuadro amplio del día (DayDetail, abajo)
          es el contenido principal — sin columnas estrechas. */}
      {view === "week" && (
        <DayColumnsGrid
          startDate={rangeCursor}
          days={7}
          byDate={byDate}
          unavailByDate={unavailByDate}
          todayIso={todayISO()}
          selectedDate={selectedDate}
          onSelect={setSelectedDate}
        />
      )}
      {view === "biweek" && (
        <DayColumnsGrid
          startDate={rangeCursor}
          days={14}
          byDate={byDate}
          unavailByDate={unavailByDate}
          todayIso={todayISO()}
          selectedDate={selectedDate}
          onSelect={setSelectedDate}
        />
      )}

      {(milestonesByDate[selectedDate] || []).map((m) => (
        <div
          key={m.id}
          data-testid={`ciclo-day-banner-${m.id}`}
          className="rounded-xl border-2 border-red-400 bg-red-50 px-4 py-2.5 flex items-center gap-2 text-sm font-bold text-red-800"
        >
          <span className="inline-block h-2 w-2 rounded-full bg-red-600 animate-pulse shrink-0" />
          07:00 · CICLO DGT: {m.title}{m.ciclo_number ? ` — Ciclo ${m.ciclo_number}` : " — nº de ciclo pendiente"} · hora límite {m.time_limit || "23:59"}
        </div>
      ))}

      <DayDetail
        selectedDate={selectedDate}
        items={selectedList}
        unavailabilities={selectedUnavail}
        teachers={teachers}
        loading={loading}
        metricsById={metricsById}
        actions={actions}
        onPrevDay={view === "day" ? prev : null}
        onNextDay={view === "day" ? next : null}
        onNewOnThisDate={openNew}
        onPreview={(a) => setPreviewAppt(a)}
        onReload={load}
        onAcceptRequest={async (a) => {
          try {
            await api.post(`/appointments/${a.id}/accept-request`);
            toast.success("Propuesta aceptada — cita confirmada");
            load();
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Error");
          }
        }}
        onModifyAndConfirm={(a) => {
          // Prellena el AppointmentDialog con la propuesta del alumno.
          // La bandera _resolveChangeRequest se procesa en onSaved para
          // limpiar requested_* y marcar la cita como confirmed.
          setCreating({
            ...a,
            date: a.requested_date || a.date,
            time: a.requested_time || a.time,
            end_time: a.requested_end_time || a.end_time,
            _resolveChangeRequest: true,
          });
        }}
        onCounterPropose={(a) => setCounterFor(a)}
        onQuickModify={(a) => setEditingQuick(a)}
        onCancelChangeRequest={async (a) => {
          try {
            await api.post(`/appointments/${a.id}/cancel-change-request`);
            toast.success("Propuesta cancelada — cita en horario original");
            load();
          } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Error");
          }
        }}
      />

      <CicloDgtPanel onChanged={load} />

      <CalendarLegend />

      <AppointmentDialog
        open={creating !== null}
        appointment={creating && (creating.id || creating.student_id) ? creating : null}
        initialDate={creating?.date || selectedDate}
        students={students}
        onClose={() => setCreating(null)}
        onSaved={onSaved}
      />

      <BulkWhatsAppDialog
        open={!!peekBulkStudent}
        onClose={() => setPeekBulkStudent(null)}
        studentId={peekBulkStudent?.id || ""}
        studentName={peekBulkStudent?.name || ""}
        phone={peekBulkStudent?.phone || ""}
      />

      {availStudent && (
        <StudentAvailabilityDialog
          student={availStudent}
          onClose={() => setAvailStudent(null)}
        />
      )}

      {scheduleStudent && (
        <StudentSummaryDialog
          student={scheduleStudent}
          initialTab="horarios"
          onClose={() => setScheduleStudent(null)}
        />
      )}

      <WhatsAppPreviewDialog
        open={!!previewAppt}
        appointment={previewAppt}
        onClose={() => setPreviewAppt(null)}
        onSent={load}
      />

      <CounterProposeDialog
        open={!!counterFor}
        appointment={counterFor}
        mode="counter"
        onClose={() => setCounterFor(null)}
        onSaved={() => { setCounterFor(null); load(); }}
        onSendWhatsapp={(a) => actions.sendWhatsapp(a)}
      />

      {/* V1.XX · "Modificar" abre el AppointmentDialog completo (todos los
          campos editables: profesor, vehículo, fecha, horas, tipo,
          ubicación, observaciones, estado). */}
      <AppointmentDialog
        open={!!editingQuick}
        appointment={editingQuick}
        initialDate={editingQuick?.date || selectedDate}
        students={students}
        onClose={() => setEditingQuick(null)}
        onSaved={() => { setEditingQuick(null); load(); }}
      />
    </div>
  );
}
